import { motion } from "framer-motion";

export type BreathPhase = "idle" | "inhale" | "hold" | "exhale" | "rest";

interface BreathingCircleProps {
  phase: BreathPhase;
  /** Countdown seconds remaining in the current phase */
  phaseSec: number;
  /** Translated label for the current phase (e.g. "Inhale", "Segure") */
  label: string;
}

const PHASE_COLORS: Record<BreathPhase, string> = {
  inhale: "hsl(187 72% 50%)",  // cyan
  hold:   "hsl(265 60% 60%)",  // purple
  exhale: "hsl(142 55% 55%)",  // green
  rest:   "hsl(218 14% 30%)",  // dim
  idle:   "hsl(218 14% 25%)",
};

const PHASE_SCALE: Record<BreathPhase, number> = {
  inhale: 1.4,
  hold:   1.4,
  exhale: 0.8,
  rest:   0.8,
  idle:   1.0,
};

const PHASE_DURATION: Record<BreathPhase, number> = {
  inhale: 4,
  hold:   0.3,
  exhale: 4,
  rest:   0.3,
  idle:   0.3,
};

const BreathingCircle = ({ phase, phaseSec, label }: BreathingCircleProps) => {
  const color = PHASE_COLORS[phase];
  const scale = PHASE_SCALE[phase];
  const duration = PHASE_DURATION[phase];

  return (
    <div className="flex flex-col items-center">
      <motion.div
        animate={{
          scale,
          boxShadow: phase !== "idle"
            ? `0 0 60px ${color}40, 0 0 120px ${color}15`
            : "none",
        }}
        transition={{ duration, ease: "easeInOut" }}
        className="w-[160px] h-[160px] rounded-full flex items-center justify-center mb-4"
        style={{
          border: `3px solid ${color}`,
          background: `${color}08`,
        }}
      >
        <div className="text-center">
          <p className="font-display text-[28px] font-bold text-white">
            {phase !== "idle" ? phaseSec : ""}
          </p>
          <p
            className="font-display text-[11px] tracking-[0.15em] uppercase"
            style={{ color }}
          >
            {label}
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default BreathingCircle;
